/**
 * AppointmentList
 * Filterable table of appointments.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "@jourfx/convex/client";
import { format, startOfToday, endOfToday, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { de } from "date-fns/locale";
import { Link } from "@tanstack/react-router";
import { Search, Filter, ChevronRight, Mail, XCircle, Download, ArrowUpDown } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Input,
  Button,
  Skeleton,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@jourfx/ui";
import { useAuth } from "@/hooks/useAuth";

const STATUS_VALUES = ["all", "scheduled", "confirmed", "in_progress", "completed", "cancelled", "no_show"] as const;

type StatusValue = (typeof STATUS_VALUES)[number];

type DateRangeOption = "today" | "week" | "next7" | "month" | "past" | "custom" | "all";

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  scheduled: "outline",
  confirmed: "default",
  in_progress: "secondary",
  completed: "secondary",
  cancelled: "destructive",
  no_show: "destructive",
};

const STATUS_I18N_KEYS: Record<string, string> = {
  scheduled: "status.scheduled",
  confirmed: "status.confirmed",
  in_progress: "status.inProgress",
  completed: "status.completed",
  cancelled: "status.cancelled",
  no_show: "status.noShow",
};

const STATUS_PRIORITY: Record<string, number> = {
  confirmed: 1,
  scheduled: 2,
  in_progress: 3,
  completed: 4,
  cancelled: 5,
  no_show: 6,
};

export function AppointmentList() {
  const { t } = useTranslation("appointments");
  const { t: tc } = useTranslation("common");
  const { user } = useAuth();
  const isGutachter = user?.role === "gutachter";
  const canBulkActions = user ? ["assistent", "administrator", "super_admin"].includes(user.role) : false;

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusValue>("all");
  const [gutachterFilter, setGutachterFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<DateRangeOption>("all");
  const [customStart, setCustomStart] = useState<string>("");
  const [customEnd, setCustomEnd] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortKey, setSortKey] = useState<
    "scheduledAt" | "versicherterName" | "gutachterName" | "templateName" | "status"
  >("scheduledAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const gutachterList = useQuery(
    api.appointments.index.listGutachter,
    isGutachter ? "skip" : undefined
  );

  const dateRangeArgs = useMemo(() => {
    if (dateRange === "today") {
      return { start: startOfToday().getTime(), end: endOfToday().getTime() };
    }
    if (dateRange === "week") {
      const start = startOfWeek(new Date(), { weekStartsOn: 1 });
      const end = endOfWeek(new Date(), { weekStartsOn: 1 });
      return { start: start.getTime(), end: end.getTime() };
    }
    if (dateRange === "next7") {
      const start = startOfToday();
      const end = addDays(start, 7);
      return { start: start.getTime(), end: end.getTime() };
    }
    if (dateRange === "month") {
      const start = startOfMonth(new Date());
      const end = endOfMonth(new Date());
      return { start: start.getTime(), end: end.getTime() };
    }
    if (dateRange === "past") {
      return { start: 0, end: Date.now() };
    }
    if (dateRange === "custom" && customStart && customEnd) {
      return {
        start: new Date(customStart).getTime(),
        end: new Date(customEnd).getTime(),
      };
    }
    return undefined;
  }, [dateRange, customStart, customEnd]);

  const appointments = useQuery(api.appointments.index.list, {
    organizationId: user?.organization?.id,
    status: statusFilter === "all" ? undefined : statusFilter,
    gutachterId: gutachterFilter === "all" ? undefined : gutachterFilter,
    search: search || undefined,
    dateRange: dateRangeArgs,
  } as Record<string, unknown>);

  const updateStatus = useMutation(api.appointments.index.updateStatus);
  const sendReminder = useAction(api.appointments.index.sendReminder);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const isLoading = appointments === undefined;

  useEffect(() => {
    setSelectedIds(new Set());
    setPage(1);
  }, [search, statusFilter, gutachterFilter, dateRange, customStart, customEnd, pageSize]);

  const sortedAppointments = useMemo(() => {
    if (!appointments) return [];
    const sorted = [...appointments].sort((a: any, b: any) => {
      switch (sortKey) {
        case "versicherterName":
          return (a.versicherterName || "").localeCompare(b.versicherterName || "");
        case "gutachterName":
          return (a.gutachterName || "").localeCompare(b.gutachterName || "");
        case "templateName":
          return (a.templateName || "").localeCompare(b.templateName || "");
        case "status":
          return (STATUS_PRIORITY[a.status] ?? 99) - (STATUS_PRIORITY[b.status] ?? 99);
        case "scheduledAt":
        default:
          return a.scheduledAt - b.scheduledAt;
      }
    });
    return sortDirection === "asc" ? sorted : sorted.reverse();
  }, [appointments, sortKey, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedAppointments.length / pageSize));
  const pagedAppointments = sortedAppointments.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllOnPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        pagedAppointments.forEach((apt: any) => next.add(apt._id as string));
      } else {
        pagedAppointments.forEach((apt: any) => next.delete(apt._id as string));
      }
      return next;
    });
  };

  const handleBulkReminder = async () => {
    for (const id of selectedIds) {
      await sendReminder({ appointmentId: id as any, channel: "email" });
    }
  };

  const handleBulkCancel = async () => {
    for (const id of selectedIds) {
      await updateStatus({ appointmentId: id as any, status: "cancelled" });
    }
  };

  const handleExportCsv = () => {
    const rows = pagedAppointments.map((apt: any) => [
      format(new Date(apt.scheduledAt), "dd.MM.yyyy, HH:mm", { locale: de }),
      apt.versicherterName ?? "",
      apt.gutachterName ?? "",
      apt.templateName ?? "",
      STATUS_I18N_KEYS[apt.status] ? tc(STATUS_I18N_KEYS[apt.status]) : apt.status,
    ]);

    const header = [
      t("list.columns.dateTime"),
      t("list.columns.versicherter"),
      t("list.columns.examiner"),
      t("list.columns.reason"),
      t("list.columns.status"),
    ];
    const csv = [header, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "termine.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const renderSortHeader = (label: string, key: typeof sortKey) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground"
      onClick={() => toggleSort(key)}
    >
      {label}
      <ArrowUpDown className={`h-3.5 w-3.5 ${sortKey === key ? "text-foreground" : "text-muted-foreground"}`} />
    </button>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t("list.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t("list.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as StatusValue)}
            >
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder={t("list.allStatus")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("list.allStatus")}</SelectItem>
                {STATUS_VALUES.filter((v) => v !== "all").map((value) => (
                  <SelectItem key={value} value={value}>
                    {STATUS_I18N_KEYS[value] ? tc(STATUS_I18N_KEYS[value]) : value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!isGutachter && (
            <div className="flex items-center gap-2">
              <Select
                value={gutachterFilter}
                onValueChange={setGutachterFilter}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder={t("list.allExaminers")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("list.allExaminers")}</SelectItem>
                  {gutachterList?.map((g: any) => (
                    <SelectItem key={g._id} value={g._id as string}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Select
              value={dateRange}
              onValueChange={(value) => setDateRange(value as DateRangeOption)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t("list.dateRange")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("list.dateRange")}</SelectItem>
                <SelectItem value="today">{t("list.dateRangeToday")}</SelectItem>
                <SelectItem value="week">{t("list.dateRangeWeek")}</SelectItem>
                <SelectItem value="next7">{t("list.dateRangeNext7")}</SelectItem>
                <SelectItem value="month">{t("list.dateRangeMonth")}</SelectItem>
                <SelectItem value="past">{t("list.dateRangePast")}</SelectItem>
                <SelectItem value="custom">{t("list.dateRangeCustom")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {user?.organization && (
            <div className="flex items-center gap-2">
              <Select value={user.organization.id} disabled>
                <SelectTrigger className="w-[200px] bg-muted/40 text-muted-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={user.organization.id}>
                    {user.organization.name}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {dateRange === "custom" && (
          <div className="flex flex-wrap gap-2">
            <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
            <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
          </div>
        )}

        {canBulkActions && selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-2">
            <span className="text-sm">{t("list.selection", { count: selectedIds.size })}</span>
            <Button variant="outline" size="sm" onClick={handleBulkReminder}>
              <Mail className="mr-2 h-4 w-4" /> {t("list.sendReminder")}
            </Button>
            <Button variant="destructive" size="sm" onClick={handleBulkCancel}>
              <XCircle className="mr-2 h-4 w-4" /> {t("list.cancelSelected")}
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCsv}>
              <Download className="mr-2 h-4 w-4" /> {t("list.export")}
            </Button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {canBulkActions && (
                    <input
                      type="checkbox"
                      onChange={(e) => selectAllOnPage(e.target.checked)}
                      checked={pagedAppointments.length > 0 && pagedAppointments.every((apt: any) => selectedIds.has(apt._id as string))}
                    />
                  )}
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {renderSortHeader(t("list.columns.dateTime"), "scheduledAt")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {renderSortHeader(t("list.columns.versicherter"), "versicherterName")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {renderSortHeader(t("list.columns.examiner"), "gutachterName")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {renderSortHeader(t("list.columns.reason"), "templateName")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {renderSortHeader(t("list.columns.status"), "status")}
                </th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                  {t("list.columns.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b">
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-4" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-28" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-32" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-28" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-28" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="ml-auto h-8 w-8" />
                    </td>
                  </tr>
                ))
              ) : pagedAppointments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                    {t("list.noResults")}
                  </td>
                </tr>
              ) : (
                pagedAppointments.map((apt: any) => (
                  <tr
                    key={apt._id}
                    className="border-b transition-colors hover:bg-muted/30"
                  >
                    <td className="px-4 py-3">
                      {canBulkActions && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(apt._id as string)}
                          onChange={() => toggleSelection(apt._id as string)}
                        />
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {format(new Date(apt.scheduledAt), "dd.MM.yyyy, HH:mm", { locale: de })} Uhr
                    </td>
                    <td className="px-4 py-3">
                      {apt.versicherterName ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      {apt.gutachterName ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      {apt.templateName ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_VARIANT[apt.status] ?? "outline"}>
                        {STATUS_I18N_KEYS[apt.status] ? tc(STATUS_I18N_KEYS[apt.status]) : apt.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="icon" asChild>
                        <Link to="/appointments/$appointmentId" params={{ appointmentId: apt._id as string }}>
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-3">
            <span>
              {t("list.page", { current: page, total: totalPages })}
            </span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs text-muted-foreground"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              {t("list.prev")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              {t("list.next")}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
